import { ProjectBranchConfigResponse } from '@/app/models/projectBranchConfig';
import {
  useCreateProjectBranchConfigMutation,
  useUpdateProjectBranchConfigMutation,
} from '@/app/services/projectBranchConfigApi';
import PageTitle from '@/components/pageTitle';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface NewEditBranchResultCenterScreenProps {
  branchResultCenter: ProjectBranchConfigResponse | null;
  setDrawerOpen: (open: boolean) => void;
}

function NewEditBranchResultCenterScreen(props: NewEditBranchResultCenterScreenProps) {
  const { t } = useTranslation(['branchResultCenter', 'common']);

  const { branchResultCenter, setDrawerOpen } = props;

  const [project, setProject] = useState(branchResultCenter?.project.code || '');
  const [branch, setBranch] = useState(branchResultCenter?.branch.code || '');

  const disableCreate = !(project && branch);
  const disableUpdate =
    disableCreate ||
    (project === branchResultCenter?.project.code && branch === branchResultCenter?.branch.code);

  const [createProjectBranchConfig] = useCreateProjectBranchConfigMutation();
  const [updateProjectBranchConfig] = useUpdateProjectBranchConfigMutation();

  const handleProcess = async () => {
    if (branchResultCenter && project && branch) {
      const response = await updateProjectBranchConfig({
        id: branchResultCenter.id,
        projectCode: project,
        branchCode: branch,
      });
      if ('data' in response) {
        toast.success(
          t('updated.project.branch.config', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      }
    } else if (project && branch) {
      const response = await createProjectBranchConfig({
        projectCode: project,
        branchCode: branch,
      });
      if ('data' in response) {
        toast.success(
          t('added.project.branch.config', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      }
    }
  };

  return (
    <div
      style={{
        width: 480,
        backgroundColor: '#FAFAFA',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: '24px',
      }}
    >
      <PageTitle title={t('branch.result.center')} />
      <div style={{ marginBottom: '24px' }} />
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: 2,
          marginTop: 3,
        }}
      >
        <Box sx={{ flexGrow: 1, width: '50%' }}>
          <Typography variant="h3">
            <span style={{ color: 'orange' }}> * </span>
            {t('branch')}
          </Typography>
          <TextField
            value={branch}
            onChange={(e) => {
              setBranch(e.target.value);
            }}
            variant="outlined"
            size="small"
            placeholder={t('select.branch') as string}
            sx={{ width: '100%', fontSize: '13px', marginTop: '4px' }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: 2,
          marginTop: 3,
        }}
      >
        <Box sx={{ flexGrow: 1, width: '50%' }}>
          <Typography variant="h3">
            <span style={{ color: 'orange' }}> * </span>
            {t('result.center')}
          </Typography>
          <TextField
            value={project}
            onChange={(e) => {
              setProject(e.target.value);
            }}
            variant="outlined"
            size="small"
            placeholder={t('select.result.center') as string}
            sx={{ width: '100%', fontSize: '13px', marginTop: '4px' }}
          />
        </Box>
      </Box>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginBottom: '8px',
          marginTop: 'auto',
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="baseline"
          margin={3}
          spacing={2}
        >
          <Button
            variant="secondary"
            onClick={() => {
              setDrawerOpen(false);
            }}
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleProcess();
              setDrawerOpen(false);
            }}
            disabled={branchResultCenter ? disableUpdate : disableCreate}
          >
            {t('common:save')}
          </Button>
        </Stack>
      </div>
    </div>
  );
}

export default NewEditBranchResultCenterScreen;
